import { useCallback, useEffect, useMemo, useState } from "react";
import api from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import SitesManager from "../components/SitesManager";
import WorkersManager from "../components/WorkersManager";
import AttendanceLog from "../components/AttendanceLog";
import Logo from "../components/Logo";
import DigestNotifier from "../components/DigestNotifier";
import SLAHeatmap from "../components/SLAHeatmap";
import TeamSLADashboard from "../components/TeamSLADashboard";
import ReportsTab from "../components/ReportsTab";
import AdminsManager from "../components/AdminsManager";
import CleaningTasksManager from "../components/CleaningTasksManager";
import AssistantTab from "../components/AssistantTab";
import LegalFooter from "../components/LegalFooter";
import { LogOut, Users, Building2, History, Flame, Trophy, FileSpreadsheet, ShieldCheck, ClipboardList, Sparkles } from "lucide-react";
import logger from "../lib/logger";

const TABS = [
  { id: "sites", label: "Sites", icon: Building2 },
  { id: "workers", label: "Team Members", icon: Users },
  { id: "cleaning-tasks", label: "Cleaning Tasks", icon: ClipboardList },
  { id: "attendance", label: "Attendance", icon: History },
  { id: "heatmap", label: "SLA Heatmap", icon: Flame },
  { id: "team-sla", label: "Team SLA", icon: Trophy },
  { id: "reports", label: "Reports", icon: FileSpreadsheet },
  { id: "assistant", label: "AI Assistant", icon: Sparkles },
  { id: "admins", label: "Administrators", icon: ShieldCheck },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [sites, setSites] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState(null);
  const [shifts, setShifts] = useState({});
  const [tab, setTab] = useState("sites");
  const [assigning, setAssigning] = useState(null); // { day, hour }
  const [genLoading, setGenLoading] = useState(false);
  const [conflicts, setConflicts] = useState([]);

  const workersById = useMemo(
    () => Object.fromEntries(workers.map((w) => [w.id, w])),
    [workers]
  );

  const conflictKeys = useMemo(() => {
    const set = new Set();
    conflicts.forEach((c) => set.add(`${c.worker_id}|${c.day}|${c.hour}`));
    return set;
  }, [conflicts]);

  const loadSites = useCallback(async () => {
    try {
      const { data } = await api.get("/sites");
      setSites(data);
    } catch (err) {
      logger.error("Failed to load sites:", err);
      toast.error("Could not load sites");
    }
  }, []);

  const loadWorkers = useCallback(async () => {
    try {
      const { data } = await api.get("/workers");
      setWorkers(data);
    } catch (err) {
      logger.error("Failed to load workers:", err);
      toast.error("Could not load team members");
    }
  }, []);

  const loadConflicts = useCallback(async () => {
    try {
      const { data } = await api.get("/schedule/conflicts");
      setConflicts(data.conflicts || []);
    } catch (err) {
      logger.error("Failed to load conflicts:", err);
    }
  }, []);

  const loadShifts = useCallback(async (siteId) => {
    if (!siteId) {
      setShifts({});
      return;
    }
    try {
      const { data } = await api.get("/schedule", { params: { site_id: siteId } });
      const map = {};
      data.forEach((s) => { map[`${s.day}|${s.hour}`] = s; });
      setShifts(map);
    } catch (err) {
      logger.error("Failed to load shifts:", err);
      toast.error("Could not load schedule");
    }
  }, []);

  useEffect(() => {
    loadSites();
    loadWorkers();
    loadConflicts();
  }, [loadSites, loadWorkers, loadConflicts]);

  // Keep selectedSiteId in sync with loaded sites.
  useEffect(() => {
    if (!sites.length) return;
    if (!selectedSiteId || !sites.find((s) => s.id === selectedSiteId)) {
      setSelectedSiteId(sites[0].id);
    }
  }, [sites, selectedSiteId]);

  useEffect(() => {
    loadShifts(selectedSiteId);
  }, [selectedSiteId, loadShifts]);

  const assignWorker = useCallback(async ({ site_id, worker_id }) => {
    if (!assigning) return;
    try {
      await api.put("/schedule/cell", {
        site_id: site_id || selectedSiteId,
        day: assigning.day,
        hour: assigning.hour,
        worker_id,
      });
      setAssigning(null);
      // Reload the currently-displayed site's shifts. If the admin assigned
      // to a *different* site than the one currently shown, reload that one
      // explicitly so the new cell renders.
      const targetSite = site_id || selectedSiteId;
      if (targetSite !== selectedSiteId) {
        setSelectedSiteId(targetSite);
      } else {
        loadShifts(selectedSiteId);
      }
      loadConflicts();
    } catch (err) {
      logger.error("Assign cell failed:", err);
      if (err?.response?.status === 409) {
        toast.error(err.response.data?.detail || "Conflict: team member already booked at this hour");
      } else {
        toast.error("Failed to assign");
      }
    }
  }, [assigning, selectedSiteId, loadShifts, loadConflicts]);

  const generate = useCallback(async (allSites) => {
    setGenLoading(true);
    try {
      const params = allSites ? {} : { site_id: selectedSiteId };
      const { data } = await api.post("/schedule/generate", null, { params });
      toast.success(`Generated ${data.cells_created} shifts`);
      loadShifts(selectedSiteId);
      loadConflicts();
    } catch (err) {
      logger.error("Generation failed:", err);
      toast.error(err?.response?.data?.detail || "Generation failed");
    } finally {
      setGenLoading(false);
    }
  }, [selectedSiteId, loadShifts, loadConflicts]);

  const [clearLoading, setClearLoading] = useState(false);
  const clearSchedule = useCallback(async (allSites) => {
    setClearLoading(true);
    try {
      const params = allSites ? {} : { site_id: selectedSiteId };
      const { data } = await api.delete("/schedule", { params });
      toast.success(
        allSites
          ? `Cleared ${data.deleted} shifts across all sites`
          : `Cleared ${data.deleted} shifts for this site`,
      );
      loadShifts(selectedSiteId);
      loadConflicts();
    } catch (err) {
      logger.error("Clear schedule failed:", err);
      toast.error(err?.response?.data?.detail || "Couldn't clear schedule");
    } finally {
      setClearLoading(false);
    }
  }, [selectedSiteId, loadShifts, loadConflicts]);

  const selectedSite = sites.find((s) => s.id === selectedSiteId);

  return (
    <div className="min-h-screen flex flex-col bg-[#FAFAFA] text-gray-900">
      <header className="border-b border-gray-300 bg-white">
        <div className="flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-3">
            <Logo size="sm" />
            <div>
              <div className="font-black tracking-tighter text-lg leading-none">
                AVIDITY <span className="text-gray-400 font-medium">· ShiftGrid</span>
              </div>
              <div className="text-[10px] uppercase tracking-widest text-gray-500">
                Admin Console · Cleaning Services
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <DigestNotifier />
            <span className="text-xs text-gray-500 font-mono">{user?.email}</span>
            <button
              onClick={logout}
              data-testid="logout-button"
              className="px-3 py-1.5 border border-gray-300 text-sm flex items-center gap-1.5 hover:bg-gray-100 rounded-sm"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>

        <div className="flex gap-px bg-gray-200 border-t border-gray-300">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`flex-1 sm:flex-none px-6 py-2.5 text-sm font-semibold flex items-center justify-center gap-2 transition ${
                tab === t.id ? "bg-white text-[#002FA7]" : "bg-gray-100 text-gray-600 hover:bg-white"
              }`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="p-6">
        {tab === "sites" && (
          <div className="max-w-2xl">
            <SitesManager sites={sites} onChange={loadSites} />
          </div>
        )}

        {tab === "workers" && (
          <div className="max-w-2xl">
            <WorkersManager workers={workers} onChange={loadWorkers} sites={sites} />
          </div>
        )}

        {tab === "attendance" && <AttendanceLog workers={workers} sites={sites} />}

        {tab === "heatmap" && <SLAHeatmap />}

        {tab === "team-sla" && <TeamSLADashboard />}

        {tab === "reports" && <ReportsTab />}

        {tab === "assistant" && <AssistantTab />}

        {tab === "admins" && <AdminsManager />}

        {tab === "cleaning-tasks" && <CleaningTasksManager sites={sites} />}
      </main>

      <LegalFooter className="mt-auto" />
    </div>
  );
}
